/* eslint-disable no-nested-ternary */
import { Box, Chip, Typography } from '@mui/material';
import { ReactElement, useEffect, useState } from 'react';
import {
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
  YouTubePreviewComponent,
} from 'shared/components/AppComponents';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import LockIcon from '@mui/icons-material/Lock';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import { extractYouTubeId } from 'shared/helpers/ExtractYoutubeId';
import CheckIcon from '@mui/icons-material/Check';
import InfoIcon from '@mui/icons-material/Info';
import { TrainingPorgramsService } from 'services/TrainingProgramService';
import { Utils } from 'shared/helpers';

const ProgressBar = ({ value }: any): ReactElement => (
  <Box
    sx={{
      width: '100%',
      height: 4,
      background: '#E5E7EB',
      borderRadius: 2,
      overflow: 'hidden',
    }}
  >
    <Box
      sx={{
        height: '100%',
        width: `${value}%`,
        background: '#d1f27a',
        borderRadius: 2,
        transition: 'width 0.4s cubic-bezier(0.4,0,0.2,1)',
      }}
    />
  </Box>
);

const StepBadge = ({ type }: any): any => {
  const label = type.charAt(0).toUpperCase() + type.slice(1);
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        background: type === 'video' ? '#06362B' : 'white',
        color: type === 'video' ? 'white' : '#06362B',
        border: '1px solid lightgray',
      }}
    />
  );
};

const TextStep = ({ step }: any): any => (
  <Box sx={{ padding: '28px 32px' }}>
    <Typography
      sx={{
        fontSize: 22,
        fontWeight: 800,
        color: '#3d3c42',
        lineHeight: 1.4,
      }}
      mb={3}
    >
      {step.title}
    </Typography>
    <Box
      sx={{
        padding: '24px 28px',
        borderRadius: '10px',
        background: '#F9FAFB',
        border: '1.5px solid #E5E7EB',
        lineHeight: 1.8,
        color: '#374151',
        fontSize: 15,
      }}
      dangerouslySetInnerHTML={{ __html: step.content }}
    />
  </Box>
);

const DotPagination = ({ total, current }: any): any => (
  <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
    {Array.from({ length: total }).map((_, i) => (
      <Box
        key={i}
        sx={{
          width: i === current ? 18 : 7,
          height: 7,
          borderRadius: '4px',
          background: i === current ? '#d1f27a' : '#D1D5DB',
          transition: 'all 0.3s ease',
        }}
      />
    ))}
  </Box>
);

const VideoStep = ({ step, purpose }: any): any => {
  const videoId = step.youtubeUrl ? extractYouTubeId(step.youtubeUrl) : null;
  const previewError = Boolean(step.youtubeUrl && !videoId);

  return (
    <Box
      sx={{
        padding: '28px 32px',
        ...(purpose === 'external'
          ? {
              justifyItems: 'center',
              placeItems: 'center',
              textAlign: 'center',
            }
          : {}),
      }}
    >
      <Typography
        style={{
          fontSize: 22,
          fontWeight: 800,
          color: '#3d3c42',
          lineHeight: 1.4,
        }}
        mb={3}
      >
        {step.title}
      </Typography>
      <YouTubePreviewComponent
        videoId={videoId}
        previewLoading={false}
        previewError={previewError}
        purpose={purpose}
      />
    </Box>
  );
};

const QuizStep = ({
  step,
  onPass,
  onSubmit,
  initialAnswers,
  initialSubmitted,
  initialResult,
  initialAttempt,
}: any): any => {
  const { questions, settings } = step;
  const [answers, setAnswers] = useState<any>(initialAnswers ?? {});
  const [submitted, setSubmitted] = useState<boolean>(
    initialSubmitted ?? false
  );
  const [result, setResult] = useState<any>(initialResult ?? null);
  const [attempt, setAttempt] = useState<number>(initialAttempt ?? 1);

  const handleMCSelect = (qId: any, optId: any): void => {
    if (submitted) return;
    setAnswers((prev: any) => ({ ...prev, [qId]: optId }));
  };

  const handleTrueFalseSelect = (qId: any, val: boolean): void => {
    if (submitted) return;
    setAnswers((prev: any) => ({ ...prev, [qId]: val }));
  };

  const allAnswered = questions.every(
    (q: { id: string | number }) => answers[q.id] !== undefined
  );

  const handleSubmit = (): any => {
    let correct = 0;
    questions.forEach((q: any) => {
      if (q.type === 'multiple_choice') {
        const chosen = q.options.find(
          (o: { id: any }) => o.id === answers[q.id]
        );

        if (chosen?.isCorrect) {
          correct += 1;
        }
      } else if (q.type === 'true_false') {
        if (answers[q.id] === q.correctAnswer) {
          correct += 1;
        }
      }
    });

    const passed = correct >= settings.passingScore;
    setSubmitted(true);
    setResult(passed ? 'pass' : 'fail');

    onSubmit?.({
      submittedAnswers: answers,
      lastScore: { correct, total: questions.length },
      passed,
      attemptsUsed: attempt,
    });

    if (passed) {
      setTimeout(() => onPass(), 1200);
    }
  };

  const handleRetry = (): void => {
    setAnswers({});
    setSubmitted(false);
    setResult(null);
    setAttempt((a) => a + 1);
  };

  const getOptionStyle = (q: any, opt: any): any => {
    const defaultStyle = {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '1rem',
      padding: '14px 16px',
      borderRadius: 10,
      border: '1.5px solid #E5E7EB',
      cursor: submitted ? 'default' : 'pointer',
      transition: 'all 0.15s ease',
      marginBottom: 3,
      background: '#FFFFFF',
    };
    if (!submitted) {
      if (answers[q.id] === opt.id) {
        return {
          ...defaultStyle,
          border: '1.5px solid #d1f27a',
          background: '#F1F8E6',
        };
      }
      return defaultStyle;
    }
    if (opt.isCorrect)
      return {
        ...defaultStyle,
        border: '1.5px solid #16A34A',
        background: '#F0FDF4',
      };
    if (answers[q.id] === opt.id && !opt.isCorrect)
      return {
        ...defaultStyle,
        border: '1.5px solid #DC2626',
        background: '#FEF2F2',
      };
    return { ...defaultStyle, opacity: 0.6 };
  };

  const getTrueFalseStyle = (q: any, val: any): any => {
    const defaultStyle = {
      flex: 1,
      padding: '14px',
      borderRadius: 10,
      border: '1.5px solid #E5E7EB',
      cursor: submitted ? 'default' : 'pointer',
      transition: 'all 0.15s ease',
      textAlign: 'center',
      fontWeight: 600,
      fontSize: 14,
      color: '#06362b',
      background: '#FFFFFF',
      opacity: '0.8',
    };
    if (!submitted) {
      if (answers[q.id] === val)
        return {
          ...defaultStyle,
          border: '1.5px solid #d1f27a',
          background: '#F1F8E6',
        };
      return defaultStyle;
    }
    if (val === q.correctAnswer)
      return {
        ...defaultStyle,
        border: '1.5px solid #16A34A',
        background: '#F0FDF4',
        color: '#15803D',
      };
    if (answers[q.id] === val && val !== q.correctAnswer)
      return {
        ...defaultStyle,
        border: '1.5px solid #DC2626',
        background: '#FEF2F2',
        color: '#DC2626',
      };
    return { ...defaultStyle, opacity: 0.6 };
  };

  return (
    <Box style={{ padding: '20px 32px 28px' }}>
      {/* QUIZ HEADER */}
      <Typography
        sx={{
          fontSize: 22,
          fontWeight: 800,
          color: '#3d3c42',
          lineHeight: 1.4,
        }}
        mb={3}
      >
        {step.title}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 16px',
          background: '#F9FAFB',
          borderRadius: '10px',
          marginBottom: '20px',
          border: '1px solid #D6E9B8',
        }}
      >
        <Typography sx={{ fontSize: 13, color: '#7E7E7E', fontWeight: 500 }}>
          {questions.length} questions • Pass: {settings.passingScore} correct
        </Typography>
        {attempt > 1 && (
          <Typography sx={{ fontSize: 13, color: '#7E7E7E' }}>
            Attempt #{attempt}
          </Typography>
        )}
      </Box>

      {/* RESULT */}
      {submitted && result && (
        <Box
          sx={{
            padding: '14px 18px',
            borderRadius: '10px',
            marginBottom: '20px',
            background: result === 'pass' ? '#F0FDF4' : '#FEF2F2',
            border: `1.5px solid ${result === 'pass' ? '#86EFAC' : '#FECACA'}`,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              background: result === 'pass' ? '#16A34A' : '#DC2626',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {result === 'pass' ? (
              <CheckIcon sx={{ color: 'white' }} />
            ) : (
              <CloseIcon sx={{ color: 'white' }} />
            )}
          </Box>
          <Box>
            <Box
              sx={{
                fontWeight: 700,
                fontSize: 14,
                color: result === 'pass' ? '#15803D' : '#B91C1C',
              }}
            >
              {result === 'pass'
                ? 'Congratulations! You passed!'
                : 'Not quite there yet'}
            </Box>
            <Box
              sx={{
                fontSize: 13,
                color: result === 'pass' ? '#166534' : '#991B1B',
              }}
            >
              {result === 'pass'
                ? 'Moving to the next step...'
                : `You need ${settings.passingScore} correct answers to continue.`}
            </Box>
          </Box>
        </Box>
      )}

      {/* QUESTIONS */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {questions.map(
          (
            q: {
              id: any;
              type?: any;
              options?: any;
              correctAnswer?: any;
              prompt?: any;
              explanation?: any;
            },
            qi: number
          ) => (
            <Box
              key={q.id}
              sx={{
                border: '1.5px solid #E5E7EB',
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
              }}
            >
              {/* QUESTION HEADER */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '1rem',
                  padding: '18px 20px 16px',
                  borderBottom: '1px solid #F3F4F6',
                }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: submitted
                      ? (() => {
                          let isCorrect = false;
                          if (q.type === 'multiple_choice') {
                            const chosen = q.options.find(
                              (o: { id: any }) => o.id === answers[q.id]
                            );
                            isCorrect = chosen?.isCorrect || false;
                          } else {
                            isCorrect = answers[q.id] === q.correctAnswer;
                          }
                          return isCorrect ? '#16A34A' : '#DC2626';
                        })()
                      : '#06362b',
                    color: '#FFFFFF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 13,
                    fontWeight: 700,
                    flexShrink: 0,
                    transition: 'background 0.3s ease',
                  }}
                >
                  {qi + 1}
                </Box>
                <Typography
                  sx={{
                    fontSize: 15,
                    fontWeight: 600,
                    color: '#06362b',
                    lineHeight: 1.5,
                    margin: 0,
                    paddingTop: '2px',
                  }}
                >
                  {q.prompt}
                </Typography>
              </Box>

              {/* OPTIONS LIST */}
              <Box sx={{ padding: '16px 20px' }}>
                {q.type === 'multiple_choice' ? (
                  <Box>
                    {q.options.map((opt: any) => (
                      <Box
                        key={opt.id}
                        sx={{
                          ...getOptionStyle(q, opt),
                          ...(!submitted &&
                            answers[q.id] !== opt.id && {
                              '&:hover': {
                                borderColor: '#A9D36B',
                                backgroundColor: '#F1F8E6',
                              },
                            }),
                        }}
                        onClick={() => handleMCSelect(q.id, opt.id)}
                      >
                        <Box
                          sx={{
                            width: 18,
                            height: 18,
                            borderRadius: '50%',
                            border: `2px solid ${
                              submitted
                                ? opt.isCorrect
                                  ? '#16A34A'
                                  : answers[q.id] === opt.id
                                  ? '#DC2626'
                                  : '#D1D5DB'
                                : answers[q.id] === opt.id
                                ? '#d1f27a'
                                : '#D1D5DB'
                            }`,
                            background: submitted
                              ? opt.isCorrect
                                ? '#16A34A'
                                : answers[q.id] === opt.id
                                ? '#DC2626'
                                : 'transparent'
                              : answers[q.id] === opt.id
                              ? '#d1f27a'
                              : 'transparent',
                            flexShrink: 0,
                            marginTop: '2px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.15s ease',
                          }}
                        >
                          {(answers[q.id] === opt.id ||
                            (submitted && opt.isCorrect)) && (
                            <Box
                              sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                background: '#FFFFFF',
                              }}
                            />
                          )}
                        </Box>
                        <Typography
                          sx={{
                            fontSize: 14,
                            color: '#06362b',
                            lineHeight: 1.5,
                            opacity: '0.8',
                          }}
                        >
                          {opt.text}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', gap: '10px' }}>
                    {[true, false].map((val) => (
                      <Box
                        key={String(val)}
                        sx={{
                          ...getTrueFalseStyle(q, val),
                          ...(!submitted &&
                            answers[q.id] !== val && {
                              '&:hover': {
                                borderColor: '#A9D36B',
                                backgroundColor: '#F1F8E6',
                              },
                            }),
                        }}
                        onClick={() => handleTrueFalseSelect(q.id, val)}
                      >
                        {val ? 'True' : 'False'}
                      </Box>
                    ))}
                  </Box>
                )}

                {/* EXPLANATION */}
                {submitted && settings.showExplanations && (
                  <Box
                    sx={{
                      marginTop: '14px',
                      padding: '12px 14px',
                      borderRadius: '8px',
                      background: '#FFFBEB',
                      border: '1px solid #FDE68A',
                      display: 'flex',
                      gap: '10px',
                      alignItems: 'flex-start',
                    }}
                  >
                    <InfoIcon sx={{ color: '#FCD34D' }} />
                    <Typography
                      sx={{
                        fontSize: 13,
                        color: '#7E7E7E',
                        margin: 0,
                        lineHeight: 1.5,
                      }}
                    >
                      {q.explanation}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )
        )}
      </Box>

      {/* SUBMIT & TRY AGAIN */}
      <Box
        sx={{
          marginTop: '24px',
          display: 'flex',
          justifyContent: 'center',
          gap: '12px',
        }}
      >
        {!submitted ? (
          <AppPrimaryButton
            disabled={!allAnswered}
            title="Submit Quiz"
            onClick={handleSubmit}
            fullWidth
          />
        ) : result === 'fail' ? (
          <AppPrimaryButtonOutlined
            title="Try Again"
            onClick={handleRetry}
            startIcon={<RestartAltIcon />}
          />
        ) : null}
      </Box>
    </Box>
  );
};

const ContentDetails = ({
  progress,
  canGoNext,
  handleNext,
  handleBack,
  setOpen,
  currentStep,
  steps,
  step,
  setQuizPassed,
  purpose = null,
  trainingDetails = null,
  handleQuizSubmit,
  savedQuizProgress,
}: any): ReactElement => (
  <>
    {/* HEADER */}
    <Box
      sx={{
        padding: '20px 28px 0',
        borderBottom: '1px solid #F3F4F6',
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '16px',
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 26,
              fontWeight: 800,
              color: '#3d3c42',
            }}
            mt={3}
          >
            {!purpose ? 'Training Preview' : trainingDetails?.title}
          </Typography>
          <Typography
            sx={{
              fontSize: 16,
              color: '#7E7E7E',
              fontWeight: 400,
            }}
            mt={3}
          >
            {!purpose
              ? 'Preview how learners will experience this training'
              : trainingDetails?.description}
          </Typography>
        </Box>

        {!purpose && (
          <Box
            onClick={() => setOpen(false)}
            sx={{
              width: 32,
              height: 32,
              borderRadius: '8px',
              border: '1px solid #E5E7EB',
              background: '#FFFFFF',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#6B7280',
              transition: 'all 0.15s ease',
            }}
          >
            <CloseIcon />
          </Box>
        )}
      </Box>

      {/* STEP DETAILS */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '12px',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#3d3c42' }}>
            Step {currentStep + 1} of {steps.length}
          </Typography>
          <StepBadge type={step.type} />
        </Box>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#76a81f' }}>
          {progress}% complete
        </Typography>
      </Box>

      <ProgressBar value={progress} />
      <Box sx={{ height: '2px' }} />
    </Box>

    {/* BODY */}

    <Box sx={{ flex: 1, overflowY: 'auto' }}>
      {step.type === 'video' && <VideoStep step={step} purpose={purpose} />}
      {step.type === 'quiz' && (
        <QuizStep
          key={currentStep}
          step={step}
          onPass={() => setQuizPassed(true)}
          initialAnswers={savedQuizProgress?.answers}
          initialSubmitted={savedQuizProgress?.submitted}
          initialResult={savedQuizProgress?.result}
          initialAttempt={savedQuizProgress?.attemptsUsed}
          {...(purpose && {
            onSubmit: (result: any) => handleQuizSubmit?.(step.id, result),
          })}
        />
      )}
      {step.type === 'text' && <TextStep step={step} />}
    </Box>

    {/* FOOTER */}

    <Box
      sx={{
        padding: '16px 28px',
        borderTop: '1px solid #F3F4F6',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
        background: '#FAFAFA',
      }}
    >
      {/* BACK */}
      <AppPrimaryButton
        title="Back"
        startIcon={<ArrowBackIosIcon />}
        onClick={handleBack}
        disabled={currentStep === 0}
      />

      {/* DOTS */}
      <DotPagination total={steps.length} current={currentStep} />

      {/* NEXT */}
      <AppPrimaryButton
        title={currentStep === steps.length - 1 ? 'Finish' : 'Next'}
        startIcon={!canGoNext ? <LockIcon /> : <></>}
        endIcon={canGoNext ? <ArrowForwardIosIcon /> : <></>}
        onClick={handleNext}
        disabled={!canGoNext}
      />
    </Box>
  </>
);

const TrainingPreviewDialog = ({
  open,
  setOpen,
  currentStep,
  setCurrentStep,
  quizPassed,
  setQuizPassed,
  setCompletedSteps,
  steps,
  purpose = null,
  trainingDetails = null,
  handleComplete = null,
  assignmentId = null,
  trainingProgress = null,
}: any): any => {
  const existingQuizProgress = trainingProgress?.quizProgress ?? {};
  const [quizProgress, setQuizProgress] = useState<any>(existingQuizProgress);
  const [savedQuizProgress, setSavedQuizProgress] = useState<any>(() => {
    const result: any = {};
    Object.keys(existingQuizProgress).forEach((stepId) => {
      const data = existingQuizProgress[stepId];
      result[stepId] = {
        answers: data.submittedAnswers,
        submitted: true,
        result: data.passed ? 'pass' : 'fail',
        attemptsUsed: data.attemptsUsed,
      };
    });
    return result;
  });

  const step = steps[currentStep];
  const progress = Math.round(((currentStep + 1) / steps.length) * 100);
  const isQuiz = step.type === 'quiz';
  const isPassed = quizPassed || savedQuizProgress[step.id]?.result === 'pass';
  const canGoNext = !isQuiz || isPassed;

  const handleQuizSubmit = (quizStepId: string, result: any): void => {
    setQuizProgress((prev: any) => ({
      ...prev,
      [quizStepId]: {
        attemptsUsed: result.attemptsUsed,
        lastScore: result.lastScore,
        passed: result.passed,
        submittedAnswers: result.submittedAnswers,
      },
    }));

    setSavedQuizProgress((prev: any) => ({
      ...prev,
      [quizStepId]: {
        answers: result.submittedAnswers,
        submitted: true,
        result: result.passed ? 'pass' : 'fail',
        attemptsUsed: result.attemptsUsed,
      },
    }));
  };

  const handleNext = (): void => {
    const isLastStep = currentStep === steps.length - 1;
    const progressPayload = {
      progress_data: {
        currentStepIndex: isLastStep ? currentStep : currentStep + 1,
        quizProgress,
      },
      status: isLastStep ? 'completed' : 'in_progress',
    };

    if (purpose === 'external') {
      TrainingPorgramsService.updateAssignment(assignmentId, progressPayload)
        .then(({ data }) => {
          console.log(data);
        })
        .catch((error) => {
          Utils.handleErrorResponse(error);
        });
    }

    if (!isLastStep) {
      setCompletedSteps((prev: any) => new Set([...prev, currentStep]));
      setCurrentStep((s: number) => s + 1);
      setQuizPassed(false);
    } else {
      if (purpose === 'external') {
        handleComplete();
      }
      setOpen(false);
    }
  };

  const handleBack = (): void => {
    if (currentStep > 0) setCurrentStep((s: number) => s - 1);
  };

  useEffect(() => {
    if (!open) return undefined;
    const handleKey = (e: { key: string }): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handleKey);

    return () => {
      window.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  return (
    <>
      {!purpose && open && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(17,24,39,0.6)',
            backdropFilter: 'blur(4px)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
            animation: 'fadeIn 0.2s ease',
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          <style>{`
            @keyframes fadeIn { from { opacity:0 } to { opacity:1 } }
            @keyframes slideUp { from { transform:translateY(24px); opacity:0 } to { transform:translateY(0); opacity:1 } }
          `}</style>

          <Box
            sx={{
              width: '100%',
              maxWidth: '860px',
              maxHeight: '90vh',
              background: '#FFFFFF',
              borderRadius: '16px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              boxShadow:
                '0 32px 80px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.05)',
              animation: 'slideUp 0.3s cubic-bezier(0.34,1.56,0.64,1)',
            }}
          >
            <ContentDetails
              progress={progress}
              canGoNext={canGoNext}
              handleNext={handleNext}
              handleBack={handleBack}
              setOpen={setOpen}
              currentStep={currentStep}
              steps={steps}
              step={step}
              setQuizPassed={setQuizPassed}
            />
          </Box>
        </Box>
      )}

      {purpose === 'external' && (
        <ContentDetails
          progress={progress}
          canGoNext={canGoNext}
          handleNext={handleNext}
          handleBack={handleBack}
          setOpen={setOpen}
          currentStep={currentStep}
          steps={steps}
          step={step}
          setQuizPassed={setQuizPassed}
          purpose={purpose}
          trainingDetails={trainingDetails}
          handleQuizSubmit={handleQuizSubmit}
          savedQuizProgress={savedQuizProgress[step.id]}
          trainingProgress={trainingProgress}
        />
      )}
    </>
  );
};

export default TrainingPreviewDialog;
